import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  JOINTS,
  MOMENT_JOINTS,
  PLANES,
  differentiate,
  normAngle,
  planeMoments,
  type MomentJoint,
  type PlaneKey,
  type Side,
} from '@/domain/hs1'
import { Button, Card, Select } from '@/components/ui'
import { CycleChart, type Corridor, type Series } from '@/sim/CycleChart'
import { StagePanel } from '@/components/stage'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * Simulation — die Kurven über den Zyklus, neben der Figur, die sie fährt.
 *
 * DIE ANORDNUNG IST DIE AUSSAGE DER SEITE.
 *
 * Diagramme in einem Raster nebeneinander sind eine Galerie: acht Bilder, die
 * gleich wichtig aussehen, und man liest sie von links oben nach rechts unten
 * wie eine Zeitung. Eine Galerie beantwortet die Frage nicht, wegen der man
 * eine Kinetik aufmacht — nämlich WAS die Maschine in dem Augenblick tut, in
 * dem die Kurve ihre Spitze hat.
 *
 * Deshalb steht die Bühne links und bleibt beim Blättern stehen, und die
 * Kurven laufen rechts untereinander durch. Alle tragen dieselbe Abspielmarke,
 * die dieselbe Stelle zeigt wie die Figur daneben. Eine Spitze im
 * Kniemoment und eine angehaltene Figur in gebeugter Standmitte sind
 * nebeneinander eine Aussage; untereinander auf zwei Seiten sind sie zwei
 * Behauptungen.
 *
 * Untereinander und nicht nebeneinander, weil alle Kurven DIESELBE waagerechte
 * Achse haben. Gestapelt liegen 0 %, 25 %, 50 % senkrecht übereinander, und man
 * liest eine Spitze über alle Kanäle hinweg an einer einzigen senkrechten
 * Linie ab. Nebeneinander muss man dafür die Augen über zwei Achsen führen,
 * die zufällig gleich beschriftet sind.
 *
 * VIER SORTEN VON HINTERGRUND stehen in jedem Bild, und sie sagen
 * Verschiedenes — Standphase, Normband, Grenze, Überschreitung. Was sie im
 * Einzelnen bedeuten, steht am Kopf von `CycleChart`.
 */

const JOINT_LABELS: Record<MomentJoint, string> = {
  ankle: 'Sprunggelenk',
  knee: 'Knie',
  hip: 'Hüfte',
  shoulder: 'Schulter',
  elbow: 'Ellbogen',
}

export function ChannelsPage() {
  const { state, simulation, phase, setFocus } = useSimulation()
  const { gait, dynamics, metrics, limits } = simulation

  const [joint, setJoint] = useState<MomentJoint>('knee')
  const [plane, setPlane] = useState<PlaneKey>('sag')
  const [side, setSide] = useState<Side | 'both'>('R')

  const n = dynamics.samples

  const angles = useMemo(() => {
    const build = (s: Side) => {
      const table = gait.angles[joint]?.[plane]?.[s]
      return table ? differentiate(table, n, dynamics.cycleTime) : null
    }
    return { R: build('R'), L: build('L') }
  }, [gait, joint, plane, n, dynamics.cycleTime])

  const moments = useMemo(
    () => ({ R: planeMoments(dynamics, joint, 'R'), L: planeMoments(dynamics, joint, 'L') }),
    [dynamics, joint],
  )

  const sides: Side[] = side === 'both' ? ['R', 'L'] : [side]
  /* Warm gegen kalt: zwei kühle Töne nebeneinander lassen sich in einer Kurve
     nicht auseinanderhalten, zwei benachbarte Farbtöne erst recht nicht. */
  const tone = (s: Side): 1 | 5 => (s === 'R' ? 1 : 5)

  const series = (pick: (s: Side) => Float32Array | null): Series[] => {
    const out: Series[] = []
    for (const s of sides) {
      const values = pick(s)
      if (values) out.push({ key: s, label: s === 'R' ? 'Rechts' : 'Links', values, tone: tone(s) })
    }
    return out
  }

  /*
    DIE AUSWAHL MARKIERT SICH SELBST. Auf dieser Seite wird das Gelenk nicht
    angeklickt, sondern gewählt — und eine Wahl, die im Bild nicht sichtbar
    wird, lässt dieselbe Frage offen wie eine Tabellenzeile: WO sitzt das,
    wovon die Kurve handelt?

    Bei beiden Seiten steht die Marke rechts. Zwei Ringe für eine Auswahl wären
    zwei Marken für einen Kanal; die rechte Seite ist die referenzierte, so wie
    im ganzen Simulator.
  */
  useEffect(() => {
    const only: Side = side === 'both' ? 'R' : side
    setFocus({
      joint,
      side: only,
      label: `${JOINT_LABELS[joint]} · ${PLANES[plane].label} · ${only === 'R' ? 'rechts' : 'links'}`,
    })
    /* Beim Verlassen der Seite wieder abnehmen: die Marke gehört zu dieser
       Auswahl und nicht zur Sitzung. */
    return () => setFocus(null)
  }, [joint, plane, side, setFocus])

  const limit = limits[joint]?.[plane] ?? null

  /*
    DAS NORMBAND — NUR BEI EINER SEITE, und das ist keine Sparsamkeit.

    Es ist der Schlauch um EINE aufgezeichnete Mittelkurve. Bei zwei gezeigten
    Seiten gäbe es zwei davon, sie lägen um eine halbe Periode versetzt
    übereinander, und die Schnittfläche sähe aus wie ein drittes Band, das
    nichts bedeutet. Wer den Vergleich mit der Aufzeichnung will, schaut sich
    eine Seite an; wer die Symmetrie prüft, braucht das Band nicht.

    Es steht ausserdem nur unter WINKELN. Ein Normband unter einem Moment wäre
    die Streuung einer Winkelaufzeichnung unter einer gerechneten Grösse —
    zwei verschiedene Dinge in einem Bild.
  */
  const corridor = useMemo<Corridor | undefined>(() => {
    if (sides.length !== 1) return undefined
    const definition = JOINTS[joint]
    const curve = definition?.planes[plane]
    if (!curve) return undefined
    const only = sides[0]
    const mean = new Float32Array(n + 1)
    for (let i = 0; i <= n; i++) {
      mean[i] = normAngle(state.gait, joint, plane, only, (i % n) / n)
    }
    return { mean, sd: curve.sd, label: `Normband ±${num(curve.sd, 1)}°` }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- sides ist aus side abgeleitet */
  }, [state.gait, joint, plane, side, n])

  const grf = useMemo(() => {
    const out = new Float32Array(n + 1)
    for (let i = 0; i < n; i++) out[i] = dynamics.grf.R[i].y + dynamics.grf.L[i].y
    out[n] = out[0]
    return out
  }, [dynamics, n])

  const com = useMemo(() => {
    const out = new Float32Array(n + 1)
    for (let i = 0; i < n; i++) out[i] = dynamics.com[i].y
    out[n] = out[0]
    return out
  }, [dynamics, n])

  return (
    <div className="stack">
      <PageHead title="Simulation" />

      <div className="metrics">
        <Metric label="Geschwindigkeit" value={`${num(metrics.speed, 2)} m/s`} />
        <Metric
          label="Bodenkraft Spitze"
          value={`${num(metrics.groundPeak, 2)} KG`}
          note={`${num(metrics.groundPeak * metrics.mass * 9.81, 0)} N`}
        />
        <Metric
          label="Bodenfehler"
          value={`${num(1000 * metrics.groundError, 1)} mm`}
          note={`Schweben ${num(1000 * metrics.groundFloat, 1)} mm`}
          warn={Math.abs(metrics.groundError) > 0.01 || metrics.groundFloat > 0.02}
        />
        <Metric
          label="Impulsbilanz"
          value={`${num(100 * simulation.balance.verticalError, 2)} %`}
          note="senkrecht, über den Zyklus"
          warn={Math.abs(simulation.balance.verticalError) > 0.05}
        />
      </div>

      {/*
        EINE ZEILE, KEINE KARTE.

        Was hier steht, ist keine Aussage, sondern ein Bedienelement: welches
        Gelenk, welche Ebene, welche Seite. Eine Karte mit Überschrift ringsum
        macht daraus einen Abschnitt, den man liest — und kostet drei Zeilen
        Höhe über den Diagrammen, die man eigentlich sehen will.

        Rechts der Weg zu den Kanälen. Die Kanalauswahl IST der Vorrat dieser
        Seite: dort wird bestimmt, welche Kurven hier stehen. Sie gehört
        deshalb an diese Zeile und nicht als eigene Kachel ins Dock.
      */}
      <div className="pickbar">
        <Select
          aria-label="Gelenk"
          value={joint}
          onChange={(e) => setJoint(e.target.value as MomentJoint)}
        >
          {MOMENT_JOINTS.map((key) => (
            <option key={key} value={key}>
              {JOINT_LABELS[key]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Ebene"
          value={plane}
          onChange={(e) => setPlane(e.target.value as PlaneKey)}
        >
          {(Object.keys(PLANES) as PlaneKey[]).map((key) => (
            <option key={key} value={key}>
              {PLANES[key].label}
            </option>
          ))}
        </Select>
        <div className="pickbar__sides">
          {(['R', 'L', 'both'] as const).map((key) => (
            <Button
              key={key}
              size="sm"
              variant={side === key ? 'primary' : 'ghost'}
              onClick={() => setSide(key)}
            >
              {key === 'R' ? 'Rechts' : key === 'L' ? 'Links' : 'Beide'}
            </Button>
          ))}
        </div>
        <Link className="pickbar__more" to="/kanaele">
          Kanäle wählen
        </Link>
      </div>

      {/*
        ZWEI SPALTEN, UND DIE LINKE KLEBT.

        Die Bühne muss beim Blättern durch die Kurven stehenbleiben, sonst ist
        sie nach dem zweiten Diagramm aus dem Bild und die Abspielmarke zeigt
        auf nichts. Auf schmalen Schirmen fällt die Anordnung untereinander —
        dort ist eine halbierte Bühne neben einer halbierten Kurve für beides
        zu wenig Platz.
      */}
      <div className="sim-split">
        <div className="sim-split__stage">
          {/* Ohne eigenes Anheften: die Spalte klebt bereits, und zwei
              ineinander geschachtelte Anheftungen sind eine zu viel. */}
          <StagePanel sticky={false} height={700} />
        </div>

        {/*
          KOMPAKT WIE DIE SPUREN UNTER „KANÄLE".

          Vorher trug jedes Diagramm eine eigene Karte: acht Rahmen, acht mal
          Innenabstand, und zwischen zwei Kurven lag mehr Rand als Kurve. Jetzt
          eine Karte, Haarlinien dazwischen und halbe Höhe — man sieht drei
          Diagramme gleichzeitig statt anderthalb, und der Vergleich zwischen
          Winkel, Moment und Leistung ist genau der Zweck dieser Seite.
        */}
        <Card flush className="sim-charts">
          <div className="simchart">
            <CycleChart
              height={132}
              title={`${JOINT_LABELS[joint]} · Winkel`}
              unit="°"
              stance={metrics.stance}
              cursor={phase}
              corridor={corridor}
              limit={limit ? { lo: limit.lo, hi: limit.hi, label: 'Bewegungsbereich' } : undefined}
              series={series((s) => angles[s]?.angle ?? null)}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title={`${JOINT_LABELS[joint]} · Winkelgeschwindigkeit`}
              unit="°/s"
              stance={metrics.stance}
              cursor={phase}
              decimals={0}
              series={series((s) => angles[s]?.velocity ?? null)}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title={`${JOINT_LABELS[joint]} · Winkelbeschleunigung`}
              unit="°/s²"
              stance={metrics.stance}
              cursor={phase}
              decimals={0}
              series={series((s) => angles[s]?.acceleration ?? null)}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title={`${JOINT_LABELS[joint]} · Moment`}
              unit="Nm"
              stance={metrics.stance}
              cursor={phase}
              limit={
                limit ? { lo: -limit.moment, hi: limit.moment, label: 'Grenzmoment' } : undefined
              }
              series={series((s) => moments[s][plane])}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title={`${JOINT_LABELS[joint]} · Gelenkleistung`}
              unit="W"
              stance={metrics.stance}
              cursor={phase}
              decimals={0}
              series={series((s) => moments[s].power)}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title="Bodenreaktionskraft, senkrecht"
              unit="N"
              stance={metrics.stance}
              cursor={phase}
              decimals={0}
              series={[{ key: 'grf', label: 'Beide Füsse', values: grf, tone: 4 }]}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title="Schwerpunkthöhe"
              unit="m"
              stance={metrics.stance}
              cursor={phase}
              decimals={3}
              zero={false}
              series={[{ key: 'com', label: 'Körperschwerpunkt', values: com, tone: 3 }]}
            />
          </div>
          <div className="simchart">
            <CycleChart
              height={132}
              title="Lastanteil je Fuss"
              unit="0 … 1"
              stance={metrics.stance}
              cursor={phase}
              decimals={2}
              series={[
                { key: 'R', label: 'Rechts', values: dynamics.load.R, tone: 1 },
                { key: 'L', label: 'Links', values: dynamics.load.L, tone: 5 },
              ]}
            />
          </div>
        </Card>
      </div>

    </div>
  )
}
